import { apiClient } from './client';

export type SizeTier = 'xs' | 'sm' | 'md' | 'lg' | 'xl'; // Removed 'full' to match frontend

export interface SizeTierConfig {
    min: number;
    preferred: number;
    max: number;
}

export const SIZE_TIERS: Record<SizeTier, SizeTierConfig> = {
    'xs': { min: 1, preferred: 2, max: 2 },
    'sm': { min: 3, preferred: 6, max: 8 },
    'md': { min: 4, preferred: 12, max: 12 },
    'lg': { min: 4, preferred: 16, max: 24 },
    'xl': { min: 4, preferred: 24, max: 24 }
};

export const CHART_WIDTH_MAP: Record<SizeTier, Record<string, number>> = {
    'xs': { xs: 2, sm: 2, md: 2, lg: 2, xl: 2 },
    'sm': { xs: 3, sm: 4, md: 5, lg: 8, xl: 6 },
    'md': { xs: 4, sm: 8, md: 12, lg: 12, xl: 12 },
    'lg': { xs: 4, sm: 8, md: 12, lg: 16, xl: 16 },
    'xl': { xs: 4, sm: 8, md: 12, lg: 24, xl: 24 },
};

export const DEFAULT_CHART_HEIGHTS: Record<SizeTier, number> = {
    'xs': 2,
    'sm': 4,
    'md': 8,
    'lg': 8,
    'xl': 12,
};

export interface BreakpointConfig {
    maxWidth: number;
    columns: number;
    minChartSize: number;
    maxHeightUnits: number;
}

export const BREAKPOINTS: Record<string, BreakpointConfig> = {
    'xs': { maxWidth: 640, columns: 4, minChartSize: 1, maxHeightUnits: 22 },
    'sm': { maxWidth: 768, columns: 8, minChartSize: 1, maxHeightUnits: 22 },
    'md': { maxWidth: 1024, columns: 12, minChartSize: 1, maxHeightUnits: 22 },
    'lg': { maxWidth: 1280, columns: 24, minChartSize: 1, maxHeightUnits: 22 },
    'xl': { maxWidth: Infinity, columns: 24, minChartSize: 1, maxHeightUnits: 22 }
};

export type TemplateType =
    | 'Financial Insights'
    | 'Decision Support'
    | 'Operational Efficiency'
    | 'Customer Behavior'
    | 'Predictive Analytics'
    | 'Risk Management'
    | 'Other';

export const TEMPLATE_TYPES: TemplateType[] = [
    'Financial Insights',
    'Decision Support',
    'Operational Efficiency',
    'Customer Behavior',
    'Predictive Analytics',
    'Risk Management',
    'Other'
];

export type ChartCategory = 'Financial' | 'Decision' | 'Operations' | 'Marketing' | 'Customer Behavior' | 'Growth' | 'Risk' | 'Prediction' | 'Expert' | 'Other';

export interface BoardTemplate {
    id: string;
    name: string;
    description?: string;
    templateType: TemplateType;
    chartConfig: ChartItem[];
    isPublic: boolean;
    userId?: string;
    createdAt: string;
    updatedAt: string;
    status?: 'draft' | 'published';
}

export interface ChartDefinition {
    type: string;
    title: string;
    description?: string;
    version: string;
    defaultSizeTier: SizeTier;
    defaultAspectRatio: string;
    categories: ChartCategory[];
    requiredData?: string[];
}

export const CHART_TYPES = {
    // Basic KPIs
    TOTAL_REVENUE: 'total_revenue',
    TOTAL_EXPENSES: 'total_expenses',
    NET_PROFIT_KPI: 'net_profit_kpi',
    ORDERS_KPI: 'orders_kpi',
    AD_SPEND_KPI: 'ad_spend_kpi',
    AOV_KPI: 'aov_kpi',

    // Profitability
    NET_PROFIT_OVER_TIME: 'net_profit_over_time',
    PROFIT_WATERFALL: 'profit_waterfall',
    PROFIT_MARGIN_OVER_TIME: 'profit_margin_over_time',
    INCOME_VS_COSTS: 'income_vs_costs',
    DAILY_PL_TABLE: 'daily_pl_table',
    RUNNING_PROFIT: 'running_profit',
    PROFIT_PERIOD_COMPARISON: 'profit_period_comparison',

    // Ad Performance
    ROAS_BY_PLATFORM: 'roas_by_platform',
    ROAS_OVER_TIME: 'roas_over_time',
    BLENDED_MER: 'blended_mer',
    AD_SPEND_PERCENT: 'ad_spend_percent',
    CPA_OVER_TIME: 'cpa_over_time',
    CAMPAIGN_ROAS_LEADERBOARD: 'campaign_roas_leaderboard',
    AD_SET_EFFICIENCY_SCATTER: 'ad_set_efficiency_scatter',
    CREATIVE_PERFORMANCE_TABLE: 'creative_performance_table',
    AD_FUNNEL: 'ad_funnel',
    CPM_TREND: 'cpm_trend',
    CPC_TREND: 'cpc_trend',
    WASTED_AD_SPEND: 'wasted_ad_spend',
    CTR_OVER_TIME: 'ctr_over_time',

    // Product Performance
    BEST_SELLERS_BY_REVENUE: 'best_sellers_by_revenue',
    REVENUE_CONCENTRATION: 'revenue_concentration',
    PRODUCT_SALES_TREND: 'product_sales_trend',
    UNITS_SOLD_RANKING: 'units_sold_ranking',
    REVENUE_PER_UNIT: 'revenue_per_unit',
    PRODUCT_RETURN_RATE: 'product_return_rate',
    REFUND_DAMAGE_BY_PRODUCT: 'refund_damage_by_product',
    PRODUCT_CHANNEL_MATRIX: 'product_channel_matrix',

    // Cost Breakdown
    EXPENSE_CATEGORY_DONUT: 'expense_category_donut',
    EXPENSE_CATEGORY_TREND: 'expense_category_trend',
    PAYMENT_PROCESSING_FEES: 'payment_processing_fees',
    PAYMENT_FEES_PERCENT: 'payment_fees_percent',
    SHIPPING_COST_OVER_TIME: 'shipping_cost_over_time',
    SHIPPING_PER_ORDER: 'shipping_per_order',
    DISCOUNT_TOTAL_AND_RATE: 'discount_total_and_rate',
    JOURNAL_EXPENSE_BREAKDOWN: 'journal_expense_breakdown',
    JOURNAL_EXPENSES_OVER_TIME: 'journal_expenses_over_time',
    TRUE_COST_PER_ORDER: 'true_cost_per_order',
    EXPENSE_ANOMALY_DETECTOR: 'expense_anomaly_detector',

    // Cash Flow
    DAILY_INFLOWS_OUTFLOWS: 'daily_inflows_outflows',
    CUMULATIVE_CASH_POSITION: 'cumulative_cash_position',
    AD_SPEND_VS_REVENUE_TIMING: 'ad_spend_vs_revenue_timing',
    WEEKLY_CASH_SUMMARY: 'weekly_cash_summary',
    BREAK_EVEN_DAY: 'break_even_day',
    MONEY_FLOW_SANKEY: 'money_flow_sankey',

    // Refunds
    RETURN_RATE_OVER_TIME: 'return_rate_over_time',
    REFUND_AMOUNT_OVER_TIME: 'refund_amount_over_time',
    REFUND_TYPE_BREAKDOWN: 'refund_type_breakdown',
    REFUND_TRUE_COST: 'refund_true_cost',
    MOST_RETURNED_PRODUCTS: 'most_returned_products',

    // Traffic & Attribution
    PAID_VS_ORGANIC_REVENUE: 'paid_vs_organic_revenue',
    PAID_VS_ORGANIC_TREND: 'paid_vs_organic_trend',
    REVENUE_BY_UTM_SOURCE: 'revenue_by_utm_source',
    REVENUE_BY_UTM_CAMPAIGN: 'revenue_by_utm_campaign',
    UNATTRIBUTED_REVENUE: 'unattributed_revenue',

    // Customer Insights
    PURCHASE_FREQUENCY: 'purchase_frequency',
    TOP_CUSTOMERS_BY_REVENUE: 'top_customers_by_revenue',
    CUSTOMER_CONCENTRATION: 'customer_concentration',
    NEW_VS_RETURNING_REVENUE: 'new_vs_returning_revenue',
    AVG_REVENUE_PER_CUSTOMER: 'avg_revenue_per_customer',

    // Growth & Scaling
    REVENUE_PERIOD_COMPARISON: 'revenue_period_comparison',
    ORDER_VOLUME_OVER_TIME: 'order_volume_over_time',
    AOV_TREND: 'aov_trend',
    REVENUE_VS_AD_SPEND_GROWTH: 'revenue_vs_ad_spend_growth',
    SPEND_VS_ROAS_SCATTER: 'spend_vs_roas_scatter',
    SALES_BY_DAY_OF_WEEK: 'sales_by_day_of_week',

    // Forecasting
    REVENUE_FORECAST: 'revenue_forecast',
    EXPENSE_FORECAST: 'expense_forecast',
    PROFIT_FORECAST: 'profit_forecast',
    BREAK_EVEN_PROJECTION: 'break_even_projection',
    SEASONAL_INDEX: 'seasonal_index',
    WHAT_IF_SIMULATOR: 'what_if_simulator',

    // Campaign & Ad Changes
    ACTIVITY_TIMELINE: 'activity_timeline',
    REVENUE_WITH_ACTION_MARKERS: 'revenue_with_action_markers',
    BUDGET_CHANGE_IMPACT: 'budget_change_impact',
    BUDGET_INCREASE_EFFICIENCY: 'budget_increase_efficiency',
    CAMPAIGN_TOGGLE_IMPACT: 'campaign_toggle_impact',
    PAUSE_RESUME_COMPARISON: 'pause_resume_comparison',
    CREATIVE_SWAP_PERFORMANCE: 'creative_swap_performance',
    AUDIENCE_CHANGE_IMPACT: 'audience_change_impact',
    AD_APPROVAL_TRACKER: 'ad_approval_tracker',
    CAMPAIGN_LIFECYCLE: 'campaign_lifecycle',

    // Store & Product Changes
    PRODUCT_LAUNCH_PERFORMANCE: 'product_launch_performance',
    PRODUCT_REMOVAL_IMPACT: 'product_removal_impact',
    PROMO_DISCOUNT_RULE_IMPACT: 'promo_discount_rule_impact',
    REFUND_SPIKE_ANALYSIS: 'refund_spike_analysis',

    // Operational Health
    ORDER_FULFILLMENT_FUNNEL: 'order_fulfillment_funnel',
    CANCELLATION_RATE_OVER_TIME: 'cancellation_rate_over_time',
    ACTIVITY_VOLUME_VS_REVENUE: 'activity_volume_vs_revenue',
    BILLING_ISSUES_TIMELINE: 'billing_issues_timeline',
    ACTION_BREAKDOWN_BY_TYPE: 'action_breakdown_by_type',
    BEFORE_AFTER_ANY_ACTION: 'before_after_any_action',
} as const;

export const CHART_DEFINITIONS: ChartDefinition[] = [
    // ── Basic KPIs ───────────────────────────────────────────────
    { type: CHART_TYPES.TOTAL_REVENUE, title: 'Total Revenue', description: 'Total income from all sources', version: '2.0.0', defaultSizeTier: 'xs', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['metrics'] },
    { type: CHART_TYPES.TOTAL_EXPENSES, title: 'Total Expenses', description: 'Total costs across all categories', version: '2.0.0', defaultSizeTier: 'xs', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['metrics'] },
    { type: CHART_TYPES.NET_PROFIT_KPI, title: 'Net Profit', description: 'Revenue minus all expenses', version: '2.0.0', defaultSizeTier: 'xs', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['metrics'] },
    { type: CHART_TYPES.ORDERS_KPI, title: 'Orders', description: 'Total order count', version: '2.0.0', defaultSizeTier: 'xs', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['metrics'] },
    { type: CHART_TYPES.AD_SPEND_KPI, title: 'Ad Spend', description: 'Total advertising spend', version: '2.0.0', defaultSizeTier: 'xs', defaultAspectRatio: '1:1', categories: ['Financial', 'Marketing'], requiredData: ['metrics'] },
    { type: CHART_TYPES.AOV_KPI, title: 'Average Order Value', description: 'Average revenue per order', version: '2.0.0', defaultSizeTier: 'xs', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['metrics'] },

    // ── Profitability ────────────────────────────────────────────
    { type: CHART_TYPES.NET_PROFIT_OVER_TIME, title: 'Net Profit Over Time', description: 'Daily profit after all costs', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.PROFIT_WATERFALL, title: 'Profit Waterfall', description: 'Step-by-step breakdown from gross sales to net profit', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['metrics'] },
    { type: CHART_TYPES.PROFIT_MARGIN_OVER_TIME, title: 'Profit Margin % Over Time', description: 'Daily margin percentage trend', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.INCOME_VS_COSTS, title: 'Income vs Costs', description: 'Revenue line vs stacked expense categories', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.DAILY_PL_TABLE, title: 'Daily P&L Table', description: 'Day-by-day profit & loss breakdown', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.RUNNING_PROFIT, title: 'Running Profit', description: 'Cumulative profit within the period', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.PROFIT_PERIOD_COMPARISON, title: 'Profit: This Period vs Last', description: 'Profit trend compared to previous period', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },

    // ── Ad Performance ───────────────────────────────────────────
    { type: CHART_TYPES.ROAS_BY_PLATFORM, title: 'ROAS by Platform', description: 'Return on ad spend per advertising platform', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['breakdown', 'transactions'] },
    { type: CHART_TYPES.ROAS_OVER_TIME, title: 'ROAS Over Time', description: 'Daily return on ad spend trend', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.BLENDED_MER, title: 'Marketing Efficiency (MER)', description: 'Total revenue divided by total ad spend', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '1:1', categories: ['Marketing'], requiredData: ['metrics', 'dailyBreakdown'] },
    { type: CHART_TYPES.AD_SPEND_PERCENT, title: 'Ad Spend as % of Revenue', description: 'How much of revenue goes to advertising', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial', 'Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.CPA_OVER_TIME, title: 'Cost per Order Over Time', description: 'Daily cost to acquire one order', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.CAMPAIGN_ROAS_LEADERBOARD, title: 'Campaign Profitability Ranking', description: 'Campaigns ranked by return on ad spend', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Marketing'], requiredData: ['breakdown', 'transactions'] },
    { type: CHART_TYPES.AD_SET_EFFICIENCY_SCATTER, title: 'Ad Set Efficiency Map', description: 'Spend vs revenue per ad set with break-even line', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '1:1', categories: ['Marketing'], requiredData: ['breakdown', 'transactions'] },
    { type: CHART_TYPES.CREATIVE_PERFORMANCE_TABLE, title: 'Creative Performance Table', description: 'Ad-level metrics: spend, impressions, clicks, CTR, CPC, ROAS', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Marketing'], requiredData: ['breakdown', 'detailedBreakdown'] },
    { type: CHART_TYPES.AD_FUNNEL, title: 'Ad Funnel: Views → Clicks → Orders', description: 'Conversion funnel from impressions to purchases', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['metrics'] },
    { type: CHART_TYPES.CPM_TREND, title: 'CPM Trend (Cost to Reach)', description: 'Daily cost per 1,000 impressions', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.CPC_TREND, title: 'Cost per Click Trend', description: 'Daily average cost per click', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.WASTED_AD_SPEND, title: 'Wasted Ad Spend', description: 'Ads with spend but zero conversions', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Marketing', 'Risk'], requiredData: ['breakdown', 'detailedBreakdown'] },
    { type: CHART_TYPES.CTR_OVER_TIME, title: 'Click-Through Rate Over Time', description: 'Daily CTR trend — creative fatigue signal', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },

    // ── Product Performance ──────────────────────────────────────
    { type: CHART_TYPES.BEST_SELLERS_BY_REVENUE, title: 'Best Sellers by Revenue', description: 'Top products ranked by total revenue', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Financial', 'Operations'], requiredData: ['breakdown'] },
    { type: CHART_TYPES.REVENUE_CONCENTRATION, title: 'Revenue Concentration (80/20)', description: 'Product revenue Pareto analysis', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Risk', 'Operations'], requiredData: ['breakdown'] },
    { type: CHART_TYPES.PRODUCT_SALES_TREND, title: 'Product Sales Trend', description: 'Revenue per product over time', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Operations'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.UNITS_SOLD_RANKING, title: 'Units Sold Ranking', description: 'Products ranked by quantity sold', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Operations'], requiredData: ['transactions'] },
    { type: CHART_TYPES.REVENUE_PER_UNIT, title: 'Revenue per Unit by Product', description: 'Average selling price per unit', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Financial'], requiredData: ['transactions'] },
    { type: CHART_TYPES.PRODUCT_RETURN_RATE, title: 'Return Rate by Product', description: 'Which products get returned most', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Risk', 'Operations'], requiredData: ['breakdown', 'detailedBreakdown'] },
    { type: CHART_TYPES.REFUND_DAMAGE_BY_PRODUCT, title: 'Refund Damage by Product', description: 'Products costing the most in refunds', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Risk'], requiredData: ['breakdown', 'detailedBreakdown'] },
    { type: CHART_TYPES.PRODUCT_CHANNEL_MATRIX, title: 'Product × Channel Matrix', description: 'Revenue heatmap: products vs store channels', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Operations'], requiredData: ['breakdown'] },

    // ── Cost Breakdown ───────────────────────────────────────────
    { type: CHART_TYPES.EXPENSE_CATEGORY_DONUT, title: 'Expense Breakdown', description: 'Proportional split of all costs', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['breakdown'] },
    { type: CHART_TYPES.EXPENSE_CATEGORY_TREND, title: 'Cost Category Trend', description: 'Each cost type stacked over time', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.PAYMENT_PROCESSING_FEES, title: 'Payment Processing Fees', description: 'Shopify and payment gateway fees', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.PAYMENT_FEES_PERCENT, title: 'Payment Fees as % of Revenue', description: 'Fee processing cut relative to revenue', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.SHIPPING_COST_OVER_TIME, title: 'Shipping Revenue Over Time', description: 'Daily shipping revenue', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.SHIPPING_PER_ORDER, title: 'Average Shipping Revenue per Order', description: 'Shipping revenue per order trend', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.DISCOUNT_TOTAL_AND_RATE, title: 'Discounts Given', description: 'Discount amounts and percentage of gross sales', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial', 'Operations'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.JOURNAL_EXPENSE_BREAKDOWN, title: 'Custom Expense Breakdown', description: 'Manual expenses by category (COGS, subs, etc.)', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '1:1', categories: ['Financial'], requiredData: ['breakdown'] },
    { type: CHART_TYPES.JOURNAL_EXPENSES_OVER_TIME, title: 'Custom Expenses Over Time', description: 'Journal and formula expenses trend', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.TRUE_COST_PER_ORDER, title: 'True Cost per Order', description: 'All-in cost per order: ads + shipping + fees + COGS', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.EXPENSE_ANOMALY_DETECTOR, title: 'Unusual Expense Spikes', description: 'Days with abnormally high costs', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Risk', 'Financial'], requiredData: ['dailyBreakdown'] },

    // ── Cash Flow ────────────────────────────────────────────────
    { type: CHART_TYPES.DAILY_INFLOWS_OUTFLOWS, title: 'Daily Money In vs Out', description: 'Green revenue bars vs red expense bars per day', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.CUMULATIVE_CASH_POSITION, title: 'Running Cash Balance', description: 'Cumulative net from day 1 of the period', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.AD_SPEND_VS_REVENUE_TIMING, title: 'Ad Spend vs Revenue Timing', description: 'The cash timing gap between spending and earning', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial', 'Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.WEEKLY_CASH_SUMMARY, title: 'Weekly Cash Flow', description: 'Total in, out, and net per week', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.BREAK_EVEN_DAY, title: 'Break-Even Day', description: 'When cumulative revenue first exceeded expenses', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.MONEY_FLOW_SANKEY, title: 'Money Flow Diagram', description: 'Revenue sources → expense categories → net profit', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Financial'], requiredData: ['breakdown', 'metrics'] },

    // ── Refunds ──────────────────────────────────────────────────
    { type: CHART_TYPES.RETURN_RATE_OVER_TIME, title: 'Return Rate Over Time', description: 'Daily percentage of orders returned', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Risk'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.REFUND_AMOUNT_OVER_TIME, title: 'Refund Amount Over Time', description: 'Daily total refund dollars', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Risk', 'Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.REFUND_TYPE_BREAKDOWN, title: 'Refund Type Breakdown', description: 'Full returns vs partial vs shipping refunds', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '1:1', categories: ['Risk'], requiredData: ['breakdown'] },
    { type: CHART_TYPES.REFUND_TRUE_COST, title: 'True Cost of Refunds', description: 'Refund amount plus wasted ad spend', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Risk', 'Financial'], requiredData: ['metrics'] },
    { type: CHART_TYPES.MOST_RETURNED_PRODUCTS, title: 'Most Returned Products', description: 'Products with highest refund amounts', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Risk', 'Operations'], requiredData: ['breakdown'] },

    // ── Traffic & Attribution ────────────────────────────────────
    { type: CHART_TYPES.PAID_VS_ORGANIC_REVENUE, title: 'Paid vs Organic Revenue', description: 'Revenue split between paid ads and organic traffic', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '1:1', categories: ['Marketing'], requiredData: ['trafficSummary'] },
    { type: CHART_TYPES.PAID_VS_ORGANIC_TREND, title: 'Paid vs Organic Trend', description: 'Daily paid and organic revenue over time', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.REVENUE_BY_UTM_SOURCE, title: 'Revenue by Traffic Source', description: 'Which traffic sources drive real revenue', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Marketing'], requiredData: ['trafficSummary'] },
    { type: CHART_TYPES.REVENUE_BY_UTM_CAMPAIGN, title: 'Revenue by Marketing Campaign', description: 'Revenue attributed to each UTM campaign', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Marketing'], requiredData: ['transactions'] },
    { type: CHART_TYPES.UNATTRIBUTED_REVENUE, title: 'Unattributed Revenue', description: 'Revenue with no traffic source tracking', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Marketing', 'Risk'], requiredData: ['dailyBreakdown'] },

    // ── Customer Insights ────────────────────────────────────────
    { type: CHART_TYPES.PURCHASE_FREQUENCY, title: 'Purchase Frequency', description: 'How many customers bought once, twice, or more', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Customer Behavior'], requiredData: ['transactions'] },
    { type: CHART_TYPES.TOP_CUSTOMERS_BY_REVENUE, title: 'Top Customers by Spend', description: 'Highest-spending customers', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Customer Behavior'], requiredData: ['transactions'] },
    { type: CHART_TYPES.CUSTOMER_CONCENTRATION, title: 'Customer Revenue Concentration', description: 'Do top 10% of customers drive most revenue?', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Risk', 'Customer Behavior'], requiredData: ['transactions'] },
    { type: CHART_TYPES.NEW_VS_RETURNING_REVENUE, title: 'New vs Repeat Customer Revenue', description: 'Revenue from first-time vs returning buyers', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Customer Behavior'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.AVG_REVENUE_PER_CUSTOMER, title: 'Average Revenue per Customer', description: 'Total revenue divided by unique customers', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '1:1', categories: ['Financial', 'Customer Behavior'], requiredData: ['metrics', 'dailyBreakdown'] },

    // ── Growth & Scaling ─────────────────────────────────────────
    { type: CHART_TYPES.REVENUE_PERIOD_COMPARISON, title: 'Revenue: This Period vs Last', description: 'Daily revenue compared to previous period', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial', 'Growth'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.ORDER_VOLUME_OVER_TIME, title: 'Order Volume Over Time', description: 'Daily order count trend', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Operations', 'Growth'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.AOV_TREND, title: 'Average Order Value Trend', description: 'Daily AOV — upsells working or over-discounting?', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Financial', 'Growth'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.REVENUE_VS_AD_SPEND_GROWTH, title: 'Revenue Growth vs Ad Spend Growth', description: 'Is revenue outpacing ad spend over time?', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Financial', 'Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.SPEND_VS_ROAS_SCATTER, title: 'Spend vs Return Efficiency', description: 'Daily scatter: does more spending still work?', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '1:1', categories: ['Marketing'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.SALES_BY_DAY_OF_WEEK, title: 'Sales by Day of Week', description: 'When do your customers buy?', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Customer Behavior', 'Operations'], requiredData: ['dailyBreakdown'] },

    // ── Forecasting ──────────────────────────────────────────────
    { type: CHART_TYPES.REVENUE_FORECAST, title: 'Revenue Forecast', description: 'Projected revenue based on recent trends', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Prediction', 'Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.EXPENSE_FORECAST, title: 'Expense Forecast', description: 'Projected costs forward', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Prediction', 'Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.PROFIT_FORECAST, title: 'Profit Forecast', description: 'Combined revenue and expense projections', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Prediction', 'Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.BREAK_EVEN_PROJECTION, title: 'Break-Even Projection', description: 'When will cumulative profit turn positive?', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Prediction', 'Financial'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.SEASONAL_INDEX, title: 'Best & Worst Performing Days', description: 'Which days consistently over/under-perform', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Prediction', 'Customer Behavior'], requiredData: ['dailyBreakdown'] },
    { type: CHART_TYPES.WHAT_IF_SIMULATOR, title: 'What-If Scenario', description: 'Simulate ad spend changes on profit', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '2:1', categories: ['Prediction', 'Decision'], requiredData: ['metrics'] },

    // ── Campaign & Ad Changes ────────────────────────────────────
    { type: CHART_TYPES.ACTIVITY_TIMELINE, title: 'Activity Timeline', description: 'All ad and product changes, color-coded', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision'], requiredData: ['eventsSummary'] },
    { type: CHART_TYPES.REVENUE_WITH_ACTION_MARKERS, title: 'Revenue with Action Markers', description: 'Daily revenue with event annotations', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Decision', 'Financial'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.BUDGET_CHANGE_IMPACT, title: 'Budget Change Impact', description: 'Revenue before vs after budget changes', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.BUDGET_INCREASE_EFFICIENCY, title: 'Budget Increase Efficiency', description: 'Incremental ROAS on budget increases', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision', 'Marketing'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.CAMPAIGN_TOGGLE_IMPACT, title: 'Campaign Launch & Pause Impact', description: 'Revenue before vs after campaign status changes', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.PAUSE_RESUME_COMPARISON, title: 'Pause/Resume Performance', description: 'Pre-pause vs post-resume performance', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.CREATIVE_SWAP_PERFORMANCE, title: 'Creative Swap Performance', description: 'CTR before vs after creative changes', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision', 'Marketing'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.AUDIENCE_CHANGE_IMPACT, title: 'Audience Change Impact', description: 'CPM before vs after targeting changes', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision', 'Marketing'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.AD_APPROVAL_TRACKER, title: 'Ad Approval Tracker', description: 'Approved vs rejected ad reviews', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '1:1', categories: ['Marketing'], requiredData: ['eventsSummary', 'dailyBreakdown'] },
    { type: CHART_TYPES.CAMPAIGN_LIFECYCLE, title: 'Campaign Lifecycle View', description: 'Full timeline per campaign: launch → changes → end', version: '2.0.0', defaultSizeTier: 'lg', defaultAspectRatio: '16:9', categories: ['Decision', 'Marketing'], requiredData: ['eventsSummary', 'dailyBreakdown'] },

    // ── Store & Product Changes ──────────────────────────────────
    { type: CHART_TYPES.PRODUCT_LAUNCH_PERFORMANCE, title: 'Product Launch Performance', description: 'Revenue from newly published products', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision', 'Operations'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.PRODUCT_REMOVAL_IMPACT, title: 'Product Removal Impact', description: 'Revenue impact of removing products', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.PROMO_DISCOUNT_RULE_IMPACT, title: 'Promotion & Discount Rule Impact', description: 'Order volume before vs after promo changes', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '3:2', categories: ['Decision', 'Operations'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.REFUND_SPIKE_ANALYSIS, title: 'Refund Spike Analysis', description: 'Orders vs refunds offset by shipping delay', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Risk'], requiredData: ['dailyBreakdown'] },

    // ── Operational Health ────────────────────────────────────────
    { type: CHART_TYPES.ORDER_FULFILLMENT_FUNNEL, title: 'Order Fulfillment Funnel', description: 'Placed → confirmed → shipped', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Operations'], requiredData: ['eventsSummary', 'dailyBreakdown'] },
    { type: CHART_TYPES.CANCELLATION_RATE_OVER_TIME, title: 'Cancellation Rate Over Time', description: 'Daily percentage of orders cancelled', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Risk', 'Operations'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.ACTIVITY_VOLUME_VS_REVENUE, title: 'Activity Volume vs Revenue', description: 'Do more changes correlate with better results?', version: '2.0.0', defaultSizeTier: 'md', defaultAspectRatio: '2:1', categories: ['Decision'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
    { type: CHART_TYPES.BILLING_ISSUES_TIMELINE, title: 'Billing & Payment Issues', description: 'Ad platform billing events and failures', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '3:2', categories: ['Risk', 'Operations'], requiredData: ['eventsSummary', 'dailyBreakdown'] },
    { type: CHART_TYPES.ACTION_BREAKDOWN_BY_TYPE, title: 'Action Breakdown by Type', description: 'How many changes by category', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '3:2', categories: ['Decision', 'Operations'], requiredData: ['eventsSummary'] },
    { type: CHART_TYPES.BEFORE_AFTER_ANY_ACTION, title: 'Before/After Any Action', description: 'Aggregate revenue impact of all actions taken', version: '2.0.0', defaultSizeTier: 'sm', defaultAspectRatio: '2:1', categories: ['Decision'], requiredData: ['dailyBreakdown', 'eventsSummary'] },
];

export interface ChartItem {
    chartId: string;
    instanceId: string;
    sizeTier: SizeTier;
    order: number;
    aspectRatio: string;
    chartName?: string;
    chartDescription?: string;
    chartVersion?: string;
    // Layout fields
    x: number;
    y: number;
    w: number;
    h: number;
    _editBp?: string;
    [key: string]: any;
}



export interface CreateTemplateRequest {
    name: string;
    description?: string;
    templateType?: TemplateType;
    chartConfig?: ChartItem[];
}

export interface UpdateTemplateRequest {
    name?: string;
    description?: string;
    templateType?: TemplateType;
    chartConfig?: ChartItem[];
}

class AdminTemplateService {
    /**
     * Get all templates (including drafts)
     */
    async getTemplates(): Promise<BoardTemplate[]> {
        const response = await apiClient.get('/admin/templates');
        return response.data.data;
    }

    /**
     * Get template by ID
     */
    async getTemplate(id: string): Promise<BoardTemplate> {
        const response = await apiClient.get(`/admin/templates/${id}`);
        return response.data.data;
    }

    /**
     * Create a new template (starts in draft mode)
     */
    async createTemplate(data: CreateTemplateRequest): Promise<BoardTemplate> {
        const response = await apiClient.post('/admin/templates', data);
        return response.data.data;
    }

    /**
     * Update a template
     */
    async updateTemplate(id: string, data: UpdateTemplateRequest): Promise<BoardTemplate> {
        const response = await apiClient.put(`/admin/templates/${id}`, data);
        return response.data.data;
    }

    /**
     * Delete a template
     */
    async deleteTemplate(id: string): Promise<void> {
        await apiClient.delete(`/admin/templates/${id}`);
    }

    /**
     * Publish a template (make visible to users)
     */
    async publishTemplate(id: string): Promise<BoardTemplate> {
        const response = await apiClient.post(`/admin/templates/${id}/publish`);
        return response.data.data;
    }

    /**
     * Unpublish a template (hide from users)
     */
    async unpublishTemplate(id: string): Promise<BoardTemplate> {
        const response = await apiClient.post(`/admin/templates/${id}/unpublish`);
        return response.data.data;
    }

    /**
     * Duplicate a template
     */
    async duplicateTemplate(id: string): Promise<BoardTemplate> {
        const response = await apiClient.post(`/admin/templates/${id}/duplicate`);
        return response.data.data;
    }

    /**
     * Export templates
     */
    async exportTemplates(ids: string[]): Promise<void> {
        const response = await apiClient.post('/admin/templates/export', { ids }, {
            responseType: 'blob'
        });

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'export.json';
        if (contentDisposition) {
            const matches = /filename="([^"]+)"/.exec(contentDisposition);
            if (matches && matches[1]) {
                filename = matches[1];
            }
        } else if (response.headers['content-type'] === 'application/zip') {
            filename = 'export.zip';
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Import templates
     */
    async importTemplates(files: File | File[]): Promise<any> {
        const formData = new FormData();
        const filesArray = Array.isArray(files) ? files : [files];
        filesArray.forEach(file => {
            formData.append('files', file);
        });

        // Assuming API_BASE_URL is defined elsewhere or apiClient is preferred.
        // For consistency with other methods using apiClient, let's adapt this to use apiClient.
        // If the user explicitly wants to switch to fetch, API_BASE_URL would need to be provided.
        // Sticking to apiClient for now to maintain consistency and avoid undefined variables.
        const response = await apiClient.post('/admin/templates/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data.data;
    }

    async bulkTemplates(action: 'delete' | 'publish', ids: string[]): Promise<any> {
        // Adapting to use apiClient for consistency, similar to other methods.
        const response = await apiClient.post('/admin/templates/bulk', { action, ids });

        // apiClient typically throws for non-2xx status codes, so explicit check might not be needed
        // but keeping the structure from the user's provided code for error handling.
        // If apiClient doesn't throw, this check would be redundant or need adjustment based on apiClient's error structure.
        if (response.status < 200 || response.status >= 300) {
            // Assuming apiClient response has a similar structure for errors if it doesn't throw
            const error = response.data; // Or response.data.error if that's the structure
            throw new Error(error.message || 'Bulk operation failed');
        }

        return response.data.data;
    }
}

export const adminTemplateService = new AdminTemplateService();
